import { fromJS, is } from 'immutable';
import { isUndefined } from 'lodash';
import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects/';

import { getIsMobile } from 'src/store/view/selectors';

import { fetchData } from './search';

import {
  APPLY_FILTERS,
  CANCEL_FILTERS,
  CHANGE_FILTER,
  CLEAR_FILTER,
  CLEAR_FILTERS,
  SET_FILTERS,
} from '../constants/filters';
import { CHANGE_SEARCH_PARAMS } from '../constants/search';
import { TOGGLE_FILTERS_PANEL } from '../constants/view';

import { getFilter, getFilters } from '../selectors/filters';
import { getLastAppliedFilters } from '../selectors/mobile';
import {
  getAdultsCount,
  getDestinationId,
  getKidsAges,
} from '../selectors/searchParams';
import { isFiltersHidden } from '../selectors/view';

import { setFiltersAction } from '../actions/filters';

import { updateURL, currentUri as URI } from '../../../utils/uriUtils';
import { saveFiltersForCancelAction } from '../actions/mobile';
import { toggleFiltersAction } from '../actions/view';
import { filterDataToURL } from '../controllers/filtersController';
import { isMobileHotelFilterOpenedSelector } from '../selectors/filtersBase';
import { setIsEmptyFiltration } from '../store/emptyFiltration/slices';
import { getFiltersFromUrl } from '../utils';
import {
  PREVIOUS_FILTERS,
  SET_PREVIOUS_FILTERS,
} from '../../../store/previousFilters/constants';
import { setPreviousFiltersAction } from '../../../store/previousFilters/actions';

const FILTER_DELAY_TIME = 1500; // 1.5s
const IMMEDIATE_FILTERS = ['prices', 'hotel_name', 'instant_confirm'];

function* delayAction(action) {
  const { type, payload = {} } = action;
  const path = payload.path || payload;
  if (type === CHANGE_FILTER && IMMEDIATE_FILTERS.indexOf(path) === -1) {
    yield delay(FILTER_DELAY_TIME);
  }
}

function* fetchDataWithDelay(action) {
  yield put(setIsEmptyFiltration(false));
  yield delayAction(action);
  yield call(fetchData);
}

function* updateFilterUriParam(filterName) {
  const filterData = yield select(getFilter, filterName);

  if (filterData) {
    const filterParams = filterDataToURL(filterName, filterData);

    URI.removeSearch(Object.keys(filterParams));
    Object.keys(filterParams).forEach((param) => {
      const val = filterParams[param];
      if (val) {
        URI.addSearch(param, val);
      }
    });
  }
}

export function* changeURL(action) {
  try {
    const isMobileHotelFilter = yield select(isMobileHotelFilterOpenedSelector);
    if (isMobileHotelFilter) return;
    const path = action.payload.path || action.payload;
    yield updateFilterUriParam(path);
    updateURL();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* stashFilters(action) {
  try {
    yield delayAction(action);
    const activeFilters = Object.keys(getFiltersFromUrl());
    const filtersData = yield select(getFilters);

    const stashedFilters = filtersData
      .filter((val, key) => {
        if (key === 'prices') {
          return ['filter_price_min', 'filter_price_max'].some(
            (name) => activeFilters.indexOf(name) !== -1,
          );
        }
        return activeFilters.indexOf(`filter_${key}`) !== -1;
      })
      .toJS();
    /**
     * Костыль, чтобы JSON.parse отработал без циклических зависимостей
     * поле name в фильтре по звездам содержит в себе react компонент
     */
    if (stashedFilters.stars) {
      stashedFilters.stars.map((starFilter) => {
        delete starFilter.name;
        return starFilter;
      });
    }

    const destinationId = yield select(getDestinationId);
    const adults = yield select(getAdultsCount);
    const kids = (yield select(getKidsAges)).toJS();
    yield sessionStorage.setItem(
      PREVIOUS_FILTERS,
      JSON.stringify({
        filters: stashedFilters,
        destinationId,
        adults,
        kids,
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* buildURL() {
  try {
    const activeFilters = Object.keys(getFiltersFromUrl());
    if (activeFilters.length) {
      URI.removeSearch(activeFilters);
    }
    const filtersData = yield select(getFilters);
    const [...filters] = filtersData.keys();
    yield filters.map(updateFilterUriParam);
    updateURL();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* saveFiltersForCancel(action) {
  try {
    const { payload } = action;
    const showFilters = isUndefined(payload)
      ? !(yield select(isFiltersHidden))
      : payload;
    if (showFilters) {
      // save filters when filters pannel toggled to show
      const currentFilters = yield select(getFilters);
      yield put(saveFiltersForCancelAction(currentFilters));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* cancelFiltersChange() {
  try {
    const lastAppliedFilters = yield select(getLastAppliedFilters);
    const currentFilters = yield select(getFilters);
    if (!is(lastAppliedFilters, currentFilters)) {
      yield put(setFiltersAction(lastAppliedFilters));
    }
    yield put(toggleFiltersAction());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function addPreviousToURL(action) {
  const filters = action.payload;

  Object.keys(filters).forEach((filterName) => {
    const filterData = filters[filterName];

    const params = filterDataToURL(filterName, fromJS(filterData));
    Object.keys(params).forEach((param) => {
      const val = params[param];
      if (val) {
        URI.addSearch(param, val);
      }
    });
  });
  updateURL();
}

function* applyFilters(action) {
  yield stashFilters(action);
  yield delay(100);
  yield put(toggleFiltersAction(false));
}

export default function* filtersSaga() {
  const isMobile = yield select(getIsMobile);
  const sagas = [
    yield takeEvery([CHANGE_FILTER, CLEAR_FILTER], changeURL),
    yield takeEvery(
      [CLEAR_FILTERS, CHANGE_SEARCH_PARAMS, SET_FILTERS],
      buildURL,
    ),
    yield takeLatest(CHANGE_FILTER, fetchDataWithDelay),
    yield takeLatest(
      [CLEAR_FILTER, CLEAR_FILTERS, SET_PREVIOUS_FILTERS, SET_FILTERS],
      fetchData,
    ),
    yield takeLatest(setPreviousFiltersAction.type, addPreviousToURL),
  ];
  if (isMobile) {
    sagas.concat([
      yield takeLatest(TOGGLE_FILTERS_PANEL, saveFiltersForCancel),
      yield takeLatest([APPLY_FILTERS], applyFilters),
      yield takeLatest(CANCEL_FILTERS, cancelFiltersChange),
    ]);
  } else {
    sagas.concat([
      yield takeLatest(
        [CLEAR_FILTER, CHANGE_FILTER, CLEAR_FILTERS],
        stashFilters,
      ),
    ]);
  }

  yield all(sagas);
}
